import React from 'react';
import { SubscriptionPlanTemplate } from '@models/subscription';
import { getSeatCountingRoleLabels } from '@constants/seatCountingRoles';
import { useTranslation } from '@hooks/useTranslation';

export type BillingPeriod = 'MONTHLY' | 'ANNUAL';

export interface PlanSelectionCardsProps {
  plans: SubscriptionPlanTemplate[];
  selectedPlanId: string | null;
  onSelectPlan: (planId: string) => void;
  billingPeriod: BillingPeriod | string;
  isLoading?: boolean;
  disabled?: boolean;
  /**
   * Heading above the cards. Sign-up overrides it to "Your plan" when only one plan is
   * public, where there is nothing to select and "Select Your Plan" would be a lie.
   */
  label?: string;
  /** Render the cards as a summary: no radio, no hover, no click. */
  readOnly?: boolean;
}

const CheckIcon: React.FC = () => (
  <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
);

const FeatureCheckIcon: React.FC = () => (
  <svg
    className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const BoltIcon: React.FC = () => (
  <svg
    className="w-4 h-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 10V3L4 14h7v7l9-11h-7z"
    />
  </svg>
);

const PlanSelectionCards: React.FC<PlanSelectionCardsProps> = ({
  plans,
  selectedPlanId,
  onSelectPlan,
  billingPeriod,
  isLoading = false,
  disabled = false,
  label,
  readOnly = false,
}) => {
  const tBillingSeats = useTranslation('billing').seats;
  const tEnumLabels = useTranslation('enumLabels');
  const tCommon = useTranslation('common');
  const headingText = label ?? 'Select Your Plan';
  const heading = (
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
      {headingText}
      {!readOnly && <span className="text-red-500"> *</span>}
    </label>
  );

  if (isLoading) {
    return (
      <div className="space-y-3">
        {heading}
        <div className="text-center py-4 text-gray-500">Loading plans...</div>
      </div>
    );
  }

  if (plans.length === 0) {
    return null;
  }

  const isAnnual = billingPeriod === 'ANNUAL';

  const handleSelectPlan = (planId: string) => {
    if (!disabled && !readOnly) {
      onSelectPlan(planId);
    }
  };

  // Always show as selectable cards with full features (same UI for single or multiple plans)
  return (
    <div className="space-y-3">
      {heading}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {plans.map((plan) => {
          const isSelected = selectedPlanId === plan.id;
          const monthlyCost = plan.basePriceCents / 100;
          const displayPrice = isAnnual ? monthlyCost * 12 : monthlyCost;
          const hasTrial = plan.trialDurationDays && plan.trialDurationDays > 0;
          const seatCountingLabels = getSeatCountingRoleLabels(
            plan.seatCountingRoles,
            tEnumLabels,
            tCommon.unknown,
          );
          // Sprint 17, story-082: cheapest linked add-on price, omitted entirely if the plan
          // has no linked add-ons yet — replaces the old "Additional users billed daily" line,
          // which didn't reflect real per-plan pricing.
          const cheapestAddonPriceCents = plan.addOns?.length
            ? Math.min(...plan.addOns.map((addon) => addon.priceCents))
            : null;

          return (
            <label
              key={plan.id}
              className={`relative flex flex-col p-5 border-2 rounded-lg transition-all ${
                readOnly ? '' : 'cursor-pointer'
              } ${
                isSelected
                  ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 shadow-md'
                  : `border-gray-300 dark:border-gray-600 ${
                      readOnly
                        ? ''
                        : 'hover:border-indigo-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => handleSelectPlan(plan.id)}>
              {!readOnly && (
                <input
                  type="radio"
                  name="selectedPlanId"
                  value={plan.id}
                  checked={isSelected}
                  onChange={() => handleSelectPlan(plan.id)}
                  disabled={disabled}
                  className="sr-only"
                />
              )}

              {/* Checkmark indicator */}
              {isSelected && !readOnly && (
                <div className="absolute top-3 right-3">
                  <CheckIcon />
                </div>
              )}

              {/* Plan name */}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 pr-8 uppercase">
                {plan.name}
              </h3>

              {/* Pricing */}
              <div className="mb-4">
                <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                  ${displayPrice.toFixed(2)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  per {isAnnual ? 'year' : 'month'}
                </p>
              </div>

              {/* Features */}
              <div className="space-y-2 flex-1">
                {/* Trial or Starts immediately */}
                {hasTrial ? (
                  <div className="flex items-start text-sm text-gray-700 dark:text-gray-300">
                    <FeatureCheckIcon />
                    <span>{plan.trialDurationDays}-day free trial</span>
                  </div>
                ) : (
                  <div className="flex items-start text-sm text-indigo-700 dark:text-indigo-300">
                    <BoltIcon />
                    <span>Starts immediately</span>
                  </div>
                )}

                {/* Included seats */}
                {plan.includedSeats > 0 && (
                  <div className="flex items-start text-sm text-gray-700 dark:text-gray-300">
                    <FeatureCheckIcon />
                    <span>
                      {(plan.includedSeats === 1
                        ? tBillingSeats.includedSeatOne
                        : tBillingSeats.includedSeatOther
                      ).replace('{n}', String(plan.includedSeats))}
                    </span>
                  </div>
                )}

                {/* Additional seat price, per plan (story-082) */}
                {cheapestAddonPriceCents !== null && (
                  <div className="flex items-start text-sm text-gray-700 dark:text-gray-300">
                    <FeatureCheckIcon />
                    <span>Additional seat ${(cheapestAddonPriceCents / 100).toFixed(2)}/mo</span>
                  </div>
                )}

                {/* Card at sign-up. Rendered from the plan flag rather than assumed, so
                    the two plans are actually distinguishable before choosing one. */}
                {plan.requiresPaymentUpfront === false ? (
                  <div className="flex items-start text-sm text-green-700 dark:text-green-400">
                    <FeatureCheckIcon />
                    <span>No credit card required to start</span>
                  </div>
                ) : (
                  <div className="flex items-start text-sm text-gray-700 dark:text-gray-300">
                    <BoltIcon />
                    <span>Credit card required at sign-up</span>
                  </div>
                )}

                {/* Roles that count as seats */}
                {seatCountingLabels.length > 0 && (
                  <div className="flex items-start text-sm text-gray-700 dark:text-gray-300">
                    <FeatureCheckIcon />
                    <span>Seats count: {seatCountingLabels.join(', ')}</span>
                  </div>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default PlanSelectionCards;
